import React, { useState, useEffect } from "react";
import { FaArrowLeftLong, FaArrowRightLong } from "react-icons/fa6";

const WhyChooseGoNextMaker = () => {
  const features = [
    {
      id: 1,
      icon: "/home-8/what/icons/1.svg",
      title: "Apprentissage hybride",
      description: "Combine les cours en ligne et des ateliers pratiques en présentiel pour apprendre à ton rythme."
    },
    {
      id: 2,
      icon: "/home-8/what/icons/2.svg",
      title: "Encadrement d'excellence",
      description: "Bénéficie du soutien de tuteurs issus des meilleures universités et fablabs ainsi que de mentors locaux passionnés."
    },
    {
      id: 3,
      icon: "/home-8/what/icons/3.svg",
      title: "Learning by Making",
      description: "Chaque module se transforme en projet concret pour apprendre en créant."
    },
    {
      id: 4,
      icon: "/home-8/what/icons/1.svg",
      title: "Réseau & Communauté internationale",
      description: "Accède à des Fablabs, Makerspaces et ateliers partenaires, et rejoins un écosystème mondial de makers : étudiants, designers, ingénieurs et artisans."
    },
    {
      id: 5,
      icon: "/home-8/what/icons/2.svg",
      title: "Certification valorisante",
      description: "Obtiens des certificats reconnus pour renforcer ton dossier académique et booster tes opportunités professionnelles."
    }
  ];

  const [currentSlide, setCurrentSlide] = useState(0);
  const [isMobile, setIsMobile] = useState(false);

  // Vérifier la taille de l'écran
  useEffect(() => {
    const checkScreenSize = () => {
      setIsMobile(window.innerWidth < 1024); // lg breakpoint
    };

    checkScreenSize();
    window.addEventListener('resize', checkScreenSize);

    return () => window.removeEventListener('resize', checkScreenSize);
  }, []);

  const nextSlide = () => {
    setCurrentSlide((prev) => (prev === features.length - 1 ? 0 : prev + 1));
  };

  const prevSlide = () => {
    setCurrentSlide((prev) => (prev === 0 ? features.length - 1 : prev - 1));
  };

  const goToSlide = (index) => {
    setCurrentSlide(index);
  };

  return (
    <section className="py-12 md:py-16 lg:py-24 bg-light-5 dark:bg-gray-900 transition-colors duration-300">
      <div className="container mx-auto px-4 md:px-20">
        {/* Section Title */}
        <div className="flex justify-center text-center mb-12 md:mb-16">
          <div className="max-w-2xl">
            <h2 className="text-3xl md:text-title font-bold text-dark-1 dark:text-white mb-4">
              Pourquoi Choisir GoNextMaker ?
            </h2>
            <p className="text-gray-600 dark:text-gray-300 text-subtitle">
              Apprends les compétences techniques pour fabriquer tes idées
            </p>
          </div>
        </div>

        {/* Content Section */}
        <div className="flex flex-col lg:flex-row gap-10 lg:gap-20  mt-12 md:mt-16">
          {/* Image */}
          <div className="w-full lg:w-1/2">
            <img
              src="/home-8/what/1.png"
              alt="Educrat platform overview"
              className="w-full h-auto rounded-lg"
            />
          </div>

          {/* Features List - Version Desktop */}
          <div className="hidden lg:block w-full lg:w-1/2">
            <div className="flex flex-col gap-8">
              {features.map((feature) => (
                <div
                  key={feature.id}
                  className="flex items-start"
                >
                  <div className="flex-shrink-0 w-16 h-16 flex items-center justify-center bg-orange-100
                  dark:bg-orange-900/30 rounded-full mr-5">
                    <img
                      src={feature.icon}
                      alt={feature.title}
                      className="w-8 h-8 flex items-center justify-center"
                    />
                  </div>
                  <div>
                    <h5 className="text-subtitle font-medium text-dark-1 dark:text-white">
                      {feature.title}
                    </h5>
                    <p className="text-gray-600 text-normal dark:text-gray-300 mt-2">
                      {feature.description}
                    </p>
                  </div>
                </div>
              ))}
            </div>

            {/* CTA Button */}
            <div className="mt-8">
              <a href="https://app.gonextmaker.com/auth/signup">
                <button className="px-[55px] py-[15px] bg-red-4 hover:bg-white text-button hover:text-red-4 border-2 border-red-4
              text-white font-medium rounded-full transition-colors duration-300">
                  Commence maintenant
                </button>
              </a>
            </div>
          </div>

          {/* Features Carousel - Version Mobile */}
          <div className="lg:hidden w-full relative">
            <div className="overflow-hidden">
              <div
                className="flex transition-transform duration-300 ease-in-out"
                style={{ transform: `translateX(-${currentSlide * 100}%)` }}
              >
                {features.map((feature) => (
                  <div key={feature.id} className="w-full flex-shrink-0 px-4">
                    <div className="flex flex-col items-center text-center p-6 bg-white dark:bg-gray-800 rounded-lg shadow-md">
                      <div className="w-16 h-16 flex items-center justify-center bg-orange-100
                      dark:bg-orange-900/30 rounded-full mb-5">
                        <img
                          src={feature.icon}
                          alt={feature.title}
                          className="w-8 h-8"
                        />
                      </div>
                      <h5 className="text-lg font-medium text-dark-1 dark:text-white mb-3">
                        {feature.title}
                      </h5>
                      <p className="text-gray-600 dark:text-gray-300">
                        {feature.description}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Navigation buttons */}
            <div className="flex items-center justify-center mt-8 space-x-4">
              <button
                onClick={prevSlide}
                className="p-3 rounded-full text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-700 transition-colors"
                aria-label="Previous testimonial"
              >
                <FaArrowLeftLong className="w-6 h-6" />
              </button>

              <div className="flex space-x-3">
                {features.map((_, index) => (
                  <button
                    key={index}
                    onClick={() => goToSlide(index)}
                    className={`w-3 h-3 rounded-full ${index === currentSlide
                      ? 'bg-red-4'
                      : 'bg-gray-300 dark:bg-gray-600'
                      }`}
                    aria-label={`Go to testimonial ${index + 1}`}
                  />
                ))}
              </div>

              <button
                onClick={nextSlide}
                className="p-3 rounded-full text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-700 transition-colors"
                aria-label="Next testimonial"
              >
                <FaArrowRightLong className="w-6 h-6" />
              </button>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default WhyChooseGoNextMaker;